import React from 'react';
import { makeStyles, Theme, createStyles, withStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import Checkbox from '@material-ui/core/Checkbox';
import Button from '@material-ui/core/Button';
import Paper from '@material-ui/core/Paper';
import { div } from 'lqx-react/lib';
import { ValidationRuleDetails } from '../../api/validationRuleDetails';

export const TransferList = ({ validationRuleDetails }: { validationRuleDetails?: ValidationRuleDetails }) => {
  const classes = useStyles();
  const notFindIndex = -1;
  const duplicationChecks = validationRuleDetails.duplicationChecks.map((item) => { return { id: item.fieldId.toString(), fieldName: item.fieldName } });
  const requiredFields = validationRuleDetails.ruleSets.filter(item => item.isRequired).map((item) => { return { id: item.fieldId.toString(), fieldName: item.fieldName } });
  const [checked, setChecked] = React.useState([]);
  const [right, setRight] = React.useState(duplicationChecks);
  const [left, setLeft] = React.useState(requiredFields.filter(item => !duplicationChecks.map((x) => { return x.id }).includes(item.id)));

  const not = (a: string[], b: string[]) => {
    return a.filter((value) => b.indexOf(value) === notFindIndex);
  }

  const intersection = (a: string[], b: string[]) => {
    return a.filter((value) => b.indexOf(value) !== notFindIndex);
  }

  const leftChecked = intersection(checked, left.map((x) => { return x.id }));
  const rightChecked = intersection(checked, right.map((x) => { return x.id }));

  const handleToggle = (value: string) => () => {
    const currentIndex = checked.indexOf(value);
    const newChecked = [...checked];

    if (currentIndex === notFindIndex) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    setChecked(newChecked);
  };

  const handleAllRight = () => {
    setRight(right.concat(left));
    setLeft([]);

    validationRuleDetails.duplicationChecks.set(requiredFields.map((item) => { return { fieldId: +item.id, fieldName: item.fieldName } }));
  };

  const handleCheckedRight = () => {
    const rightCollection = right.concat(requiredFields.filter(value => leftChecked.includes(value.id)));
    const rightCollectionWithoutDupl = Array.from(new Set(rightCollection)); //ignore duplication

    setRight(rightCollectionWithoutDupl);
    setLeft(left.filter(value => not(left.map((x) => { return x.id }), leftChecked).includes(value.id)));
    setChecked([]);

    validationRuleDetails.duplicationChecks.set(rightCollectionWithoutDupl.map((item) => { return { fieldId: +item.id, fieldName: item.fieldName } }));
  };

  const handleCheckedLeft = () => {
    const leftCollection = left.concat(requiredFields.filter(value => rightChecked.includes(value.id)))
    const leftCollectionWithoutDupl = Array.from(new Set(leftCollection)); //ignore duplication
    const rightCollection = right.filter(value => not(right.map((x) => { return x.id }), rightChecked).includes(value.id));

    setLeft(leftCollectionWithoutDupl);
    setRight(rightCollection);
    setChecked([]);

    validationRuleDetails.duplicationChecks.set(rightCollection.map((item) => { return { fieldId: +item.id, fieldName: item.fieldName } }));
  };

  const handleAllLeft = () => {
    setLeft(left.concat(right));
    setRight([]);

    validationRuleDetails.duplicationChecks.set([]);
  };

  const customList = (items) => (
    <Paper className={classes.paper}>
      <List dense component="div" role="list">
        {items.map((value) => {
          const labelId = `transfer-list-item-${value.id}-label`;

          return (
            <ListItem key={value.id} role="listitem" button onClick={handleToggle(value.id)}>
              <ListItemIcon>
                <StyleFieldCheckbox
                  checked={checked.indexOf(value.id) !== -1}
                  tabIndex={-1}
                  disableRipple
                  inputProps={{ 'aria-labelledby': labelId }}
                />
              </ListItemIcon>
              <FieldNameSection id={labelId}>{value.fieldName}</FieldNameSection>
            </ListItem>
          );
        })}
        <ListItem />
      </List>
    </Paper>
  );

  return (
    <Grid
      container
      spacing={2}
      justifyContent="center"
      alignItems="center"
      className={classes.root}
    >

      <Grid item>
        <HeaderSection>All available fields</HeaderSection>
        {customList(left)}
      </Grid>
      <Grid item>
        <Grid container direction="column" alignItems="center">
          <Button
            variant="outlined"
            size="small"
            className={classes.button}
            onClick={handleAllRight}
            disabled={left.length === 0}
            aria-label="move all right"
          >
            ≫
          </Button>
          <Button
            variant="outlined"
            size="small"
            className={classes.button}
            onClick={handleCheckedRight}
            disabled={leftChecked.length === 0}
            aria-label="move selected right"
          >
            &gt;
          </Button>
          <Button
            variant="outlined"
            size="medium"
            className={classes.button}
            onClick={handleCheckedLeft}
            disabled={rightChecked.length === 0}
            aria-label="move selected left"
          >
            &lt;
          </Button>
          <Button
            variant="outlined"
            size="medium"
            className={classes.button}
            onClick={handleAllLeft}
            disabled={right.length === 0}
            aria-label="move all left"
          >
            ≪
          </Button>
        </Grid>
      </Grid>

      <Grid item>
        <HeaderSection>Fields Used for Duplication Check</HeaderSection>
        {customList(right)}
      </Grid>
    </Grid>
  );
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      margin: 'auto',
      width: '100%'
    },
    paper: {
      width: 500,
      height: 430,
      overflow: 'auto',
      border: '1px solid whitesmoke'
    },
    button: {
      margin: '10px 5px',
      fontSize: '20px'
    },
    overrides: {
      MuiCheckbox: {
        colorSecondary: {
          color: '#custom color',
          '&$checked': {
            color: 'red',
          },
        },
      },
    },
  }),
);

const FieldNameSection = div({
  fontSize: '14px'
});

const HeaderSection = div({
  color: '#668AA5',
  fontSize: '18px',
  fontWeight: 600,
  padding: '10px 0'
});

const StyleFieldCheckbox = withStyles({
  root: {
    transform: "scale(1.3)",
    '&$checked': {
      color: '#4d9ed8',
    }
  },
  checked: {},
})(Checkbox);